import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createOrderMaster,
  discountFromProduct,
  fetchSellerOptions,
  formatOrderAmount,
  getNowOrderDateTime,
  resolveDefaultOrderStatusId,
  unitPriceFromProduct,
} from "../../api/orderApi";
import {
  formatProductPrice,
  getProductPrimaryImage,
} from "../../api/productApi";
import { resolveShopUserId } from "../../api/shopAuth";
import { getAdminUser, getShopAuthLabel } from "../../admin/utils/auth";
import { useCart } from "../../context/CartContext";

const SHIPPING_CHARGE = 0;
const PENDING_ORDER_KEY = "trivom_pending_order";

const STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

const INITIAL_FORM = {
  fullName: "",
  email: "",
  mobileNumber: "",
  alternateMobile: "",
  houseNo: "",
  street: "",
  area: "",
  city: "",
  state: "",
  pincode: "",
  paymentMethod: "",
  sellerUserId: "",
  orderStatusId: "",
};

const sectionTitleClass =
  "text-lg md:text-xl font-semibold text-gray-800 mb-4 text-lora";

function readPendingOrder() {
  try {
    const raw = sessionStorage.getItem(PENDING_ORDER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePendingOrder(payload) {
  try {
    sessionStorage.setItem(PENDING_ORDER_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota errors
  }
}

function clearPendingOrder() {
  try {
    sessionStorage.removeItem(PENDING_ORDER_KEY);
  } catch {
    // ignore
  }
}

function normalizeCheckoutState(locationState) {
  const pending = readPendingOrder();
  const state = locationState || pending;

  if (!state) return { items: [] };

  // Cart checkout: { cartItems: [{ product, quantity }] }
  if (Array.isArray(state.cartItems) && state.cartItems.length > 0) {
    return {
      items: state.cartItems
        .map((row) => ({
          product: row.product || row,
          quantity: Math.max(1, Number(row.quantity) || 1),
        }))
        .filter((row) => row.product?.product_id || row.product?.service_id),
      fromCart: true,
    };
  }

  // Single product checkout: product object itself
  if (state.product_id || state.service_id) {
    return {
      items: [{ product: state, quantity: 1 }],
      fromCart: false,
    };
  }

  if (state.product?.product_id || state.product?.service_id) {
    return {
      items: [
        {
          product: state.product,
          quantity: Math.max(1, Number(state.quantity) || 1),
        },
      ],
      fromCart: false,
    };
  }

  return { items: [] };
}

export default function CheckoutForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();

  const checkout = useMemo(
    () => normalizeCheckoutState(location.state),
    [location.state]
  );

  const [countryCode, setCountryCode] = useState("+91");
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [lineQuantities, setLineQuantities] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [buyerUserId, setBuyerUserId] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Persist checkout payload so login redirect can return here with products.
  useEffect(() => {
    if (location.state) {
      writePendingOrder(location.state);
    }
  }, [location.state]);

  useEffect(() => {
    const qtyMap = {};
    checkout.items.forEach((row, index) => {
      const id = String(row.product.product_id || row.product.service_id || index);
      qtyMap[id] = row.quantity;
    });
    setLineQuantities(qtyMap);
  }, [checkout.items]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const userId = await resolveShopUserId();
      if (cancelled) return;

      if (userId == null) {
        navigate("/login", {
          state: {
            from: "/order-page",
            message: "Please login to place your order",
          },
        });
        return;
      }

      setBuyerUserId(userId);
      const auth = getAdminUser();
      setFormData((current) => ({
        ...current,
        fullName: current.fullName || getShopAuthLabel() || "",
        mobileNumber:
          current.mobileNumber ||
          String(auth?.phone || "").replace(/\D/g, "").slice(-10),
      }));

      try {
        const [sellers, defaultStatusId] = await Promise.all([
          fetchSellerOptions(),
          resolveDefaultOrderStatusId(),
        ]);
        const providerOptions = []
        const seenProviders = new Set()
        checkout.items.forEach((row) => {
          ;(row.product?.providers || []).forEach((provider) => {
            const id = Number(provider.id)
            if (!Number.isFinite(id) || id <= 0 || seenProviders.has(id)) return
            seenProviders.add(id)
            providerOptions.push({
              value: id,
              label: provider.name || `Seller #${id}`,
            })
          })
        })

        const mergedSellers = [...providerOptions]
        sellers.forEach((seller) => {
          if (!seenProviders.has(Number(seller.value))) {
            mergedSellers.push(seller)
          }
        })

        if (cancelled) return;

        const defaultSeller =
          providerOptions[0]?.value || mergedSellers[0]?.value || "";

        setFormData((current) => ({
          ...current,
          sellerUserId: current.sellerUserId || String(defaultSeller || ""),
          orderStatusId:
            current.orderStatusId ||
            (defaultStatusId != null ? String(defaultStatusId) : ""),
        }));
      } catch (error) {
        if (!cancelled) {
          setToast({
            open: true,
            message: error.message || "Failed to load order options",
            severity: "error",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkout.items, navigate]);

  const orderLines = useMemo(
    () =>
      checkout.items.map((row, index) => {
        const id = String(
          row.product.product_id || row.product.service_id || index
        );
        const quantity = Math.max(1, Number(lineQuantities[id]) || row.quantity || 1);
        const unitPrice = unitPriceFromProduct(row.product);
        const listPrice =
          row.product?.price != null ? Number(row.product.price) : unitPrice;
        const discountPerUnit = discountFromProduct(row.product);
        return {
          id,
          product: row.product,
          quantity,
          unitPrice,
          listPrice,
          discount: discountPerUnit * quantity,
          amount: unitPrice * quantity + SHIPPING_CHARGE,
          image: getProductPrimaryImage(row.product),
        };
      }),
    [checkout.items, lineQuantities]
  );

  const orderSummary = useMemo(() => {
    const price = orderLines.reduce(
      (sum, line) => sum + line.listPrice * line.quantity,
      0
    );
    const discount = orderLines.reduce((sum, line) => sum + line.discount, 0);
    const total = orderLines.reduce((sum, line) => sum + line.amount, 0);
    return {
      price,
      discount,
      shippingCharge: SHIPPING_CHARGE,
      total,
    };
  }, [orderLines]);

  const updateField = (field, value) => {
    let processed = value;
    if (field === "mobileNumber" || field === "alternateMobile") {
      processed = value
        .replace(/\D/g, "")
        .slice(0, countryCode === "+91" ? 10 : 15);
    }
    if (field === "pincode") {
      processed = value.replace(/\D/g, "").slice(0, 6);
    }
    setFormData((current) => ({ ...current, [field]: processed }));
    setErrors((current) => ({ ...current, [field]: "" }));
  };

  const updateLineQty = (lineId, quantity) => {
    const next = Math.max(1, Math.min(20, Number(quantity) || 1));
    setLineQuantities((current) => ({
      ...current,
      [lineId]: next,
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (formData.fullName.trim().length < 2) {
      nextErrors.fullName = "Enter your full name";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Enter a valid email address";
    }
    const validMobile =
      countryCode === "+91"
        ? /^[6-9]\d{9}$/.test(formData.mobileNumber)
        : formData.mobileNumber.length >= 7;
    if (!validMobile) nextErrors.mobileNumber = "Enter a valid mobile number";
    if (
      formData.alternateMobile &&
      !(countryCode === "+91"
        ? /^[6-9]\d{9}$/.test(formData.alternateMobile)
        : formData.alternateMobile.length >= 7)
    ) {
      nextErrors.alternateMobile = "Enter a valid alternate mobile number";
    }
    if (!formData.houseNo.trim()) nextErrors.houseNo = "House number is required";
    if (!formData.street.trim()) nextErrors.street = "Street is required";
    if (!formData.area.trim()) nextErrors.area = "Area is required";
    if (!formData.city.trim()) nextErrors.city = "City is required";
    if (!formData.state) nextErrors.state = "Select your state";
    if (!/^\d{6}$/.test(formData.pincode)) {
      nextErrors.pincode = "Enter a valid 6-digit pincode";
    }
    if (!formData.paymentMethod) {
      nextErrors.paymentMethod = "Select a payment method";
    }
    if (!formData.sellerUserId) {
      nextErrors.sellerUserId = "Unable to assign a seller for this order";
    }
    if (!formData.orderStatusId) {
      nextErrors.orderStatusId = "Unable to set order status";
    }
    if (buyerUserId == null) {
      nextErrors.buyerUserId = "Please login as a buyer to place an order";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const hiddenMessage =
        nextErrors.buyerUserId ||
        nextErrors.sellerUserId ||
        nextErrors.orderStatusId;
      if (hiddenMessage) {
        setToast({
          open: true,
          message: hiddenMessage,
          severity: "error",
        });
      }
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!orderLines.length) {
      setToast({
        open: true,
        message: "Select a product from the shop before placing an order.",
        severity: "error",
      });
      return;
    }
    if (!validate()) return;

    setSubmitting(true);
    try {
      const createdOrders = [];
      for (const line of orderLines) {
        const assetId = Number(
          line.product.product_id || line.product.service_id
        );
        const payload = {
          order_datetime: getNowOrderDateTime(),
          buyer_user_id: Number(buyerUserId),
          seller_user_id: Number(formData.sellerUserId),
          asset_id: assetId,
          quantity: line.quantity,
          currency: "INR",
          unit_price: line.unitPrice,
          discount: line.discount,
          amount: line.amount,
          dispatch_address_id: null,
          delivery_address_id: null,
          shipment_tracking_id: [
            formData.houseNo.trim(),
            formData.street.trim(),
            formData.area.trim(),
            formData.city.trim(),
            formData.state,
            formData.pincode,
            `Pay:${formData.paymentMethod}`,
            `Phone:${countryCode}${formData.mobileNumber}`,
          ]
            .filter(Boolean)
            .join(", ")
            .slice(0, 250),
          order_status_id: Number(formData.orderStatusId),
          alternate_mobile: formData.alternateMobile
            ? `${countryCode}${formData.alternateMobile}`
            : "",
        };

        const created = await createOrderMaster(payload);
        createdOrders.push(created);
      }

      if (checkout.fromCart) {
        await clearCart();
      }
      clearPendingOrder();

      const firstId = createdOrders[0]?.order_id;
      setToast({
        open: true,
        message:
          createdOrders.length > 1
            ? `${createdOrders.length} orders placed successfully.`
            : `Order #${firstId} placed successfully.`,
        severity: "success",
      });
      setTimeout(() => navigate("/shop"), 1800);
    } catch (error) {
      setToast({
        open: true,
        message: error.message || "Failed to place order",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!orderLines.length) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4 text-center">
        <p className="mb-4">No product selected for checkout.</p>
        <button className="first-btn" onClick={() => navigate("/shop")}>
          Back to Shop
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="bg-[#CDA202] text-white px-4 md:px-6 py-3 flex items-center justify-between">
            <h1 className="lg:text-[24px] text-lora">Place Your Order</h1>
            <IconButton
              aria-label="Close checkout"
              className="close-btn"
              onClick={() => navigate(-1)}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="lg:p-[30px] p-5">
            <section className="mb-8">
              <h2 className={sectionTitleClass}>1. Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <TextField
                  label="Full Name"
                  value={formData.fullName}
                  onChange={(event) =>
                    updateField("fullName", event.target.value)
                  }
                  error={Boolean(errors.fullName)}
                  helperText={errors.fullName}
                  className="md:col-span-2"
                />
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                />
                <TextField
                  label="Mobile Number"
                  type="tel"
                  value={formData.mobileNumber}
                  onChange={(event) =>
                    updateField("mobileNumber", event.target.value)
                  }
                  error={Boolean(errors.mobileNumber)}
                  helperText={errors.mobileNumber}
                  InputProps={{
                    startAdornment: (
                      <select
                        aria-label="Country code"
                        value={countryCode}
                        onChange={(event) => {
                          setCountryCode(event.target.value);
                          updateField("mobileNumber", "");
                          updateField("alternateMobile", "");
                        }}
                        className="mr-2 bg-transparent border-0 outline-none"
                      >
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                      </select>
                    ),
                  }}
                />
                <TextField
                  label="Alternate Mobile Number"
                  type="tel"
                  value={formData.alternateMobile}
                  onChange={(event) =>
                    updateField("alternateMobile", event.target.value)
                  }
                  error={Boolean(errors.alternateMobile)}
                  helperText={
                    errors.alternateMobile || "Optional secondary contact number"
                  }
                  InputProps={{
                    startAdornment: (
                      <span className="mr-2 text-gray-600">{countryCode}</span>
                    ),
                  }}
                />
              </div>
            </section>

            <section className="mb-8">
              <h2 className={sectionTitleClass}>2. Delivery Address</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <TextField
                  label="House No."
                  value={formData.houseNo}
                  onChange={(event) =>
                    updateField("houseNo", event.target.value)
                  }
                  error={Boolean(errors.houseNo)}
                  helperText={errors.houseNo}
                />
                <TextField
                  label="Street"
                  value={formData.street}
                  onChange={(event) =>
                    updateField("street", event.target.value)
                  }
                  error={Boolean(errors.street)}
                  helperText={errors.street}
                />
                <TextField
                  label="Area"
                  value={formData.area}
                  onChange={(event) => updateField("area", event.target.value)}
                  error={Boolean(errors.area)}
                  helperText={errors.area}
                />
                <TextField
                  label="City"
                  value={formData.city}
                  onChange={(event) => updateField("city", event.target.value)}
                  error={Boolean(errors.city)}
                  helperText={errors.city}
                />
                <FormControl fullWidth error={Boolean(errors.state)}>
                  <InputLabel>State</InputLabel>
                  <Select
                    label="State"
                    value={formData.state}
                    onChange={(event) =>
                      updateField("state", event.target.value)
                    }
                  >
                    {STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.state && (
                    <FormHelperText>{errors.state}</FormHelperText>
                  )}
                </FormControl>
                <TextField
                  label="Pincode"
                  value={formData.pincode}
                  onChange={(event) =>
                    updateField("pincode", event.target.value)
                  }
                  error={Boolean(errors.pincode)}
                  helperText={errors.pincode}
                />
              </div>
            </section>

            <section className="mb-8">
              <h2 className={sectionTitleClass}>3. Order Summary</h2>
              <div className="border rounded-lg p-4 space-y-4">
                {orderLines.map((line) => (
                  <div
                    key={line.id}
                    className="flex flex-col sm:flex-row gap-4 pb-4 border-b last:border-0 last:pb-0"
                  >
                    {line.image ? (
                      <img
                        src={line.image}
                        alt={line.product.product_name}
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                        No image
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-3">
                        {line.product.product_name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            aria-label="Decrease quantity"
                            disabled={line.quantity <= 1}
                            onClick={() =>
                              updateLineQty(line.id, line.quantity - 1)
                            }
                            className="w-8 h-8 rounded border border-gray-300 text-gray-700 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            −
                          </button>
                          <span className="min-w-[1.5rem] text-center font-semibold">
                            {line.quantity}
                          </span>
                          <button
                            type="button"
                            aria-label="Increase quantity"
                            disabled={line.quantity >= 20}
                            onClick={() =>
                              updateLineQty(line.id, line.quantity + 1)
                            }
                            className="w-8 h-8 rounded border border-gray-300 text-gray-700 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            +
                          </button>
                        </div>
                        <span className="fw-semibold">
                          ₹{formatProductPrice(line.amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="space-y-3 pt-2">
                  <SummaryRow label="Price" value={orderSummary.price} />
                  <SummaryRow
                    label="Discount"
                    value={orderSummary.discount}
                    valueClass="text-green-600"
                    prefix="- ₹"
                  />
                  <SummaryRow
                    label="Shipping Charge"
                    value={orderSummary.shippingCharge}
                  />
                  <hr />
                  <SummaryRow
                    label="Total Amount"
                    value={orderSummary.total}
                    strong
                  />
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className={sectionTitleClass}>4. Payment Method</h2>
              <FormControl error={Boolean(errors.paymentMethod)}>
                <RadioGroup
                  row
                  value={formData.paymentMethod}
                  onChange={(event) =>
                    updateField("paymentMethod", event.target.value)
                  }
                >
                  <FormControlLabel
                    value="upi"
                    control={<Radio />}
                    label="UPI"
                  />
                  <FormControlLabel
                    value="card"
                    control={<Radio />}
                    label="Card"
                  />
                  <FormControlLabel
                    value="net_banking"
                    control={<Radio />}
                    label="Net Banking"
                  />
                </RadioGroup>
                {errors.paymentMethod && (
                  <FormHelperText>{errors.paymentMethod}</FormHelperText>
                )}
              </FormControl>
            </section>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-3 px-6 rounded-lg bg-[#cda202] hover:bg-[#B8920A] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-[18px] text-lora transition-colors"
            >
              {submitting ? "Placing Order..." : "5. Place Order"}
            </button>
          </div>
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%", maxWidth: 420 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  prefix = "₹",
  valueClass = "",
  strong = false,
}) {
  return (
    <div
      className={`flex justify-between items-center ${
        strong ? "text-lg font-bold" : "text-sm md:text-base"
      }`}
    >
      <span className="text-gray-700">{label}</span>
      <span className={valueClass}>
        {prefix}
        {formatOrderAmount(value)}
      </span>
    </div>
  );
}
